import React from "react";
import { isSameDay } from "./Utleieplan";

export interface Booking {
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  deliveryMethod?: string;
}

interface WeekCalendarProps {
  bookings: Booking[];
  weekDates: Date[];
  eventColors: string[];
}

const WeekCalendar: React.FC<WeekCalendarProps> = ({
  bookings,
  weekDates,
  eventColors,
}) => {
  const normalizeDate = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const weekStart = normalizeDate(weekDates[0]);
  const weekEnd = normalizeDate(weekDates[6]);

  // Finn relevante bookinger som overlapper uken
  const relevantBookings = bookings.filter((booking) => {
    const start = normalizeDate(booking.start);
    const end = normalizeDate(booking.end);
    return start <= weekEnd && end >= weekStart;
  });

  // Del opp i "starter i dag" og "øvrige"
  const today = normalizeDate(new Date());
  const startingToday = relevantBookings.filter((b) =>
    isSameDay(normalizeDate(b.start), today)
  );
  const others = relevantBookings.filter(
    (b) => !isSameDay(normalizeDate(b.start), today)
  );

  // Kombiner alt
  const sortedBookings = [...startingToday, ...others];

  // Beregn radene dynamisk for alle events
  const events: Array<{
    booking: Booking;
    startCol: number;
    endCol: number;
    color: string;
    spanCols: number;
    row: number;
    left: number;
    width: number;
  }> = [];

  sortedBookings.forEach((booking, index) => {
    const bookingStart = normalizeDate(booking.start);
    const bookingEnd = normalizeDate(booking.end);

    let startCol = -1;
    let endCol = -1;

    weekDates.forEach((date, colIndex) => {
      const current = normalizeDate(date);
      if (isSameDay(current, bookingStart)) {
        startCol = colIndex;
      }
      if (isSameDay(current, bookingEnd)) {
        endCol = colIndex;
      }
    });

    // Håndter events som starter før denne uken
    if (startCol === -1 && bookingStart < weekDates[0]) {
      startCol = 0;
    }

    // Håndter events som slutter etter denne uken
    if (endCol === -1 && bookingEnd > weekDates[6]) {
      endCol = 6;
    }

    if (startCol !== -1 && endCol !== -1 && startCol <= endCol) {
      const spanCols = endCol - startCol + 1;
      const leftPercent = (startCol / 7) * 100;
      const widthPercent = (spanCols / 7) * 100;

      // Finn laveste rad uten overlap
      let row = 0;
      while (true) {
        const hasOverlap = events.some(
          (e) => e.row === row && !(endCol < e.startCol || startCol > e.endCol)
        );
        if (!hasOverlap) {
          break;
        }
        row++;
      }

      events.push({
        booking,
        startCol,
        endCol,
        color: eventColors[index % eventColors.length],
        spanCols,
        row,
        left: leftPercent,
        width: widthPercent,
      });
    }
  });

  // Gruppér per rad
  const maxRow = Math.max(...events.map((e) => e.row), -1) + 1;
  const rows = Array.from({ length: maxRow }, (_, i) =>
    events.filter((e) => e.row === i)
  );

  const isToday = (date: Date) => isSameDay(normalizeDate(date), today);

  return (
    <div className="week-calendar">
      <div className="week-date-headers">
        {weekDates.map((date, index) => (
          <div
            key={index}
            className={`week-date-header ${isToday(date) ? "today" : ""}`}
          >
            {date.getDate().toString().padStart(2, "0")}
          </div>
        ))}
      </div>

      <div className="week-events-area">
        {rows.map((rowEvents, rowIndex) => (
          <div key={rowIndex} className="week-event-row">
            {rowEvents.map((event, eventIndex) => (
              <div
                key={eventIndex}
                className="week-event-bar"
                style={{
                  backgroundColor: event.color,
                  left: `${event.left}%`,
                  width: `${event.width}%`,
                  top: "2px",
                }}
                title={`${
                  event.booking.title
                } (${event.booking.start.toLocaleDateString(
                  "nb-NO"
                )} - ${event.booking.end.toLocaleDateString("nb-NO")})`}
              >
                {event.booking.title}
              </div>
            ))}
          </div>
        ))}
        {rows.length === 0 && (
          <div className="no-events">Ingen utleier denne uken</div>
        )}
      </div>

      <div className="week-body">
        {weekDates.map((date, index) => (
          <div key={index} className="week-day-slot"></div>
        ))}
      </div>
    </div>
  );
};

export default WeekCalendar;
